import React, { useState, useCallback, useEffect, memo } from "react";
import { Icon } from "@iconify/react";
import "./EventCard.css";

const EventCard = memo(({
  eventName,
  eventPoster,
  eventStartTimeStamp,
  eventEndTimeStamp,
  sponsorCode,
  countLeads = 0,
  onClickCard,
  onClickTeam,
  onClickScan,
  onClickLeads,
  onClickDownload,
  onClickShare,
  isDownloading = false,
  disableDownload = false,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Reset states when eventPoster prop changes
  useEffect(() => {
    if (eventPoster) {
      setImageLoaded(true);
      setImageError(false);
    }
  }, [eventPoster]);

  const handleCardClick = useCallback((e) => {
    // Don't navigate if clicking on action buttons or share button
    if (e.target.closest('.event-card-new__actions') || e.target.closest('.event-card-new__share-btn')) {
      return;
    }
    onClickCard?.();
  }, [onClickCard]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  return (
    <div className="event-card-new" onClick={handleCardClick}>
      {/* Event Poster */}
      <div className="event-card-new__poster">
        {(!imageLoaded || imageError || !eventPoster) && (
          <div className="event-card-new__poster-placeholder" />
        )}
        {eventPoster && !imageError && (
          <img
            src={eventPoster}
            alt={eventName}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ opacity: imageLoaded ? 1 : 0 }}
          />
        )}
      </div>

      {/* Event Info */}
      <div className="event-card-new__content">
        <div className="event-card-new__header">
          <h3 className="event-card-new__title">{eventName}</h3>
          <button
            className="event-card-new__share-btn"
            onClick={onClickShare}
            aria-label="Share event"
          >
            <Icon icon="mynaui:share" width="20" height="20" />
          </button>
        </div>

        <p className="event-card-new__date">
          {eventStartTimeStamp} - {eventEndTimeStamp}
        </p>

        {/* Action Buttons */}
        <div className="event-card-new__actions">
          <button className="event-card-new__action" onClick={onClickTeam}>
            <div className="event-card-new__action-icon">
              <Icon icon="lets-icons:group-share" width="18" height="18" />
            </div>
            <span>Team</span>
          </button>

          <button className="event-card-new__action" onClick={onClickScan}>
            <div className="event-card-new__action-icon">
              <Icon icon="lucide:scan-line" width="18" height="18" />
            </div>
            <span>Scan</span>
          </button>

          <button className="event-card-new__action" onClick={onClickLeads}>
            <div className="event-card-new__action-icon">
              <Icon icon="grommet-icons:group" width="18" height="18" />
            </div>
            <span>Leads</span>
          </button>

          <button
            className={`event-card-new__action ${disableDownload ? "event-card-new__action--disabled" : ""}`}
            onClick={disableDownload ? undefined : onClickDownload}
            disabled={disableDownload || isDownloading}
          >
            <div className="event-card-new__action-icon">
              {isDownloading ? (
                <span className="event-card-new__spinner" />
              ) : (
                <Icon icon="tdesign:download" width="18" height="18" />
              )}
            </div>
            <span>Download</span>
          </button>
        </div>

        {/* Leads Captured Badge */}
        <div className="event-card-new__leads-badge">
          Leads Captured: <span>{countLeads}</span>
        </div>
      </div>
    </div>
  );
});

EventCard.displayName = 'EventCard';

export default EventCard;
